import type { KeyboardEvent, SyntheticEvent } from 'react';
import { T, useGT, useLocale } from 'gt-react';
import { UploadSimpleIcon } from '@phosphor-icons/react';
import type { PricelistCaseFileDTO, PricelistCaseResponse } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { SectionLabel } from './pricelistDetailFields';
import { isPricelistCaseOpen } from './pricelistCaseOpen';
import { useCaseFileRoleLabel } from './pricelistCaseLabels';
import {
  formatPricelistCaseFileAddedDate,
  nextPricelistCaseFileDocumentIdForKey,
  pricelistCaseFileAddedByName,
  pricelistCaseFileNameClassName,
} from './pricelistCaseFileRowState';
import {
  PricelistCaseFileDetachButton,
  PricelistCaseFileStatusButton,
  PricelistCaseFileToolbar,
} from './PricelistCaseFileToolbar';
import { PricelistCsvView } from './PricelistCsvView';
import { pricelistCaseFilePreviewKind } from './pricelistCaseFilePreview';
import { PricelistCasePlainTextPreview } from './PricelistCasePlainTextPreview';

/**
 * A long extracted pricelist scrolls inside this height. The case page keeps
 * scrolling past the table, so History and Discussion stay a short page-scroll
 * away instead of sitting under every price row.
 */
export const PRICELIST_CASE_GRID_MAX_HEIGHT = 540;

interface Props {
  pricelistCase: PricelistCaseResponse;
  selectedFile: PricelistCaseFileDTO | undefined;
  onSelectedDocumentIdChange: (documentId: string) => void;
  csvDocumentId: string | undefined;
  isPricelistPending: boolean;
  onUploadClick: () => void;
  userNameById: ReadonlyMap<string, string>;
}

function FileTables({
  selectedFile,
  csvDocumentId,
  isPricelistPending,
}: {
  selectedFile: PricelistCaseFileDTO;
  csvDocumentId: string | undefined;
  isPricelistPending: boolean;
}) {
  const previewKind = pricelistCaseFilePreviewKind(
    selectedFile,
    csvDocumentId,
    isPricelistPending,
  );
  if (previewKind === 'pending') {
    return (
      <div className="flex flex-col gap-2 rounded-md border px-4 py-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-6 w-4/5" />
      </div>
    );
  }
  if (previewKind === 'source-text') {
    return (
      <div
        className="overflow-hidden rounded-md border"
        style={{ height: PRICELIST_CASE_GRID_MAX_HEIGHT }}
      >
        <PricelistCasePlainTextPreview documentId={selectedFile.documentId} />
      </div>
    );
  }
  if (previewKind === 'source-table') {
    return (
      <div
        className="overflow-hidden rounded-md border"
        style={{ height: PRICELIST_CASE_GRID_MAX_HEIGHT }}
      >
        <DocumentViewer
          documentId={selectedFile.documentId}
          className="h-full"
          previewPlainTextAsTable
        />
      </div>
    );
  }
  return (
    <PricelistCsvView
      csvDocumentId={csvDocumentId}
      maxGridHeight={PRICELIST_CASE_GRID_MAX_HEIGHT}
    />
  );
}

function stopRowActivation(event: SyntheticEvent) {
  event.stopPropagation();
}

function focusPricelistCaseFileRow(table: Element | null, documentId: string) {
  table
    ?.querySelector<HTMLElement>(
      `[data-document-id="${CSS.escape(documentId)}"]`,
    )
    ?.focus();
}

function PricelistCaseFileList({
  caseId,
  canEdit,
  files,
  selectedDocumentId,
  userNameById,
  onSelectedDocumentIdChange,
}: {
  caseId: string;
  canEdit: boolean;
  files: readonly PricelistCaseFileDTO[];
  selectedDocumentId: string | undefined;
  userNameById: ReadonlyMap<string, string>;
  onSelectedDocumentIdChange: (documentId: string) => void;
}) {
  const gt = useGT();
  const locale = useLocale();
  const roleLabel = useCaseFileRoleLabel();
  const documentIds = files.map((file) => file.documentId);

  function handleRowKeyDown(
    event: KeyboardEvent<HTMLTableRowElement>,
    focusedDocumentId: string,
  ) {
    const nextId = nextPricelistCaseFileDocumentIdForKey(
      documentIds,
      selectedDocumentId,
      focusedDocumentId,
      event.key,
    );
    if (nextId === undefined) return;
    event.preventDefault();
    onSelectedDocumentIdChange(nextId);
    focusPricelistCaseFileRow(event.currentTarget.closest('table'), nextId);
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table
        density="compact"
        aria-label={gt('Files on this price change', {
          context:
            'accessible name for the attached-files table on a pricelist case',
        })}
      >
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>
              <T context="pricelist case files table column: file name">File</T>
            </TableHead>
            <TableHead>
              <T context="pricelist case files table column: attachment role">
                Role
              </T>
            </TableHead>
            <TableHead>
              <T context="pricelist case files table column: deprecated status">
                Status
              </T>
            </TableHead>
            <TableHead>
              <T context="pricelist case files table column: date the file was attached">
                Added
              </T>
            </TableHead>
            <TableHead>
              <T context="pricelist case files table column: who attached the file">
                Added by
              </T>
            </TableHead>
            {canEdit ? (
              <>
                <TableHead className="w-px" />
                <TableHead className="w-px" />
              </>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((file) => {
            const selected = file.documentId === selectedDocumentId;
            return (
              <TableRow
                key={file.documentId}
                data-document-id={file.documentId}
                data-state={selected ? 'selected' : undefined}
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                className="cursor-pointer"
                onClick={() => onSelectedDocumentIdChange(file.documentId)}
                onKeyDown={(event) => handleRowKeyDown(event, file.documentId)}
              >
                <TableCell className="tabular-nums text-muted-foreground">
                  {file.position + 1}
                </TableCell>
                <TableCell>
                  <span
                    className={`block max-w-xs ${pricelistCaseFileNameClassName(file.deprecated)}`}
                    title={file.name}
                  >
                    {file.name}
                  </span>
                </TableCell>
                <TableCell>{roleLabel(file.role)}</TableCell>
                <TableCell>
                  {file.deprecated ? (
                    <Badge variant="secondary">
                      <T context="badge on a pricelist case file that should not be used">
                        Deprecated
                      </T>
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatPricelistCaseFileAddedDate(file.addedAt, locale)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {pricelistCaseFileAddedByName(
                    userNameById,
                    file.addedByUserId,
                  )}
                </TableCell>
                {canEdit ? (
                  <>
                    <TableCell
                      className="w-px"
                      onClick={stopRowActivation}
                      onKeyDown={stopRowActivation}
                    >
                      <PricelistCaseFileStatusButton
                        caseId={caseId}
                        file={file}
                      />
                    </TableCell>
                    <TableCell
                      className="w-px"
                      onClick={stopRowActivation}
                      onKeyDown={stopRowActivation}
                    >
                      <PricelistCaseFileDetachButton
                        caseId={caseId}
                        file={file}
                      />
                    </TableCell>
                  </>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export function PricelistCaseFilesPanel({
  pricelistCase,
  selectedFile,
  onSelectedDocumentIdChange,
  csvDocumentId,
  isPricelistPending,
  onUploadClick,
  userNameById,
}: Props) {
  const isOpen = isPricelistCaseOpen(pricelistCase.task);
  const canEdit = isOpen;
  const roleLabel = useCaseFileRoleLabel();

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>
          <T context="heading for the files attached to a pricelist update case">
            Files
          </T>
        </SectionLabel>
        {isOpen && (
          <Button variant="outline" size="sm" onClick={onUploadClick}>
            <UploadSimpleIcon className="h-4 w-4" />
            <T context="button on a price change that uploads a new file straight onto it">
              Upload File
            </T>
          </Button>
        )}
      </div>
      {isOpen && (
        <div className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-muted-foreground/25 px-4 py-6 text-center">
          <UploadSimpleIcon className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            <T context="visible drop zone in the Files section of an open price change">
              Drop files here to attach them to this price change
            </T>
          </p>
        </div>
      )}
      {pricelistCase.files.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="empty state when a pricelist case has no files attached">
            No files on this price change yet. Upload one here, or select them
            in the upload history and add them.
          </T>
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {selectedFile ? (
            <span
              data-testid="selected-file-role"
              className="w-fit shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            >
              {roleLabel(selectedFile.role)}
            </span>
          ) : null}
          <PricelistCaseFileList
            caseId={pricelistCase.id}
            canEdit={canEdit}
            files={pricelistCase.files}
            selectedDocumentId={selectedFile?.documentId}
            userNameById={userNameById}
            onSelectedDocumentIdChange={onSelectedDocumentIdChange}
          />
          {selectedFile ? (
            <>
              <PricelistCaseFileToolbar
                caseId={pricelistCase.id}
                file={selectedFile}
                canEdit={canEdit}
              />
              <FileTables
                selectedFile={selectedFile}
                csvDocumentId={csvDocumentId}
                isPricelistPending={isPricelistPending}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
